import React, { useState, useEffect } from 'react';
import { Check, Loader2, Sparkles, CreditCard, AlertTriangle } from 'lucide-react';
import { supabase, getSubscription } from '../services/supabase';
import { storage } from '../services/storage';
import { Subscription } from '../types';

export const Plans: React.FC = () => {
  const [loading, setLoading] = useState<'monthly' | 'yearly' | 'portal' | null>(null);
  const [isTestMode, setIsTestMode] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plantsCount, setPlantsCount] = useState(0);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [session, setSession] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setSession(session);
        const [sub, plants] = await Promise.all([
          getSubscription(session.user.id),
          storage.getPlants()
        ]);
        setSubscription(sub as any);
        setPlantsCount(plants.length);
      }
      setIsDataLoaded(true);
    };
    loadData();
  }, []);

  const handleSubscribe = async (planType: 'monthly' | 'yearly') => {
    setLoading(planType);
    try {
      if (!session?.user) throw new Error('Não autenticado');
      
      if (isTestMode) {
        window.location.href = `/?success=true&mock=true&plan=${planType}`;
        return;
      }
      
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planType,
          userId: session.user.id,
          email: session.user.email,
          priceId: planType === 'monthly' ? import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID : import.meta.env.VITE_STRIPE_YEARLY_PRICE_ID
        }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Erro ao gerar checkout');
      }
    } catch (error: any) {
      console.error(error);
      alert('Erro ao iniciar checkout: ' + (error.message || error));
    } finally {
      setLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setLoading('portal');
    try {
      if (!subscription?.stripe_customer_id) throw new Error('Customer ID não encontrado');
      const response = await fetch('/api/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: subscription.stripe_customer_id
        }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Erro ao abrir portal');
      }
    } catch (error: any) {
      console.error(error);
      alert('Erro ao acessar gerenciamento da assinatura.');
    } finally {
      setLoading(null);
    }
  };

  if (!isDataLoaded) {
    return (
      <div className="flex justify-center p-20">
        <Loader2 className="animate-spin text-raizama-action" size={32} />
      </div>
    );
  }

  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';

  return (
    <div className="animate-fade-in p-4 max-w-md mx-auto pb-24">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-raizama-action/10 rounded-[2rem] flex items-center justify-center text-raizama-action mx-auto mb-4 shadow-inner">
          <Sparkles size={32} />
        </div>
        <h2 className="text-2xl font-black text-white mb-2 uppercase tracking-tight">Assinatura</h2>
        <p className="text-sm text-raizama-textSec">Gerencie seu plano e recursos botânicos.</p>
        
                  <div className="mt-4 flex items-center justify-center gap-2">
            <label className="flex items-center cursor-pointer gap-2 bg-white/5 px-4 py-2 rounded-xl border border-white/10">
              <input 
                type="checkbox" 
                checked={isTestMode} 
                onChange={(e) => setIsTestMode(e.target.checked)}
                className="rounded text-raizama-action focus:ring-raizama-action/50 bg-black/30 border-white/20"
              />
              <span className="text-xs font-bold text-white uppercase tracking-widest">Modo Simulação (Testes)</span>
            </label>
          </div>
      </div>

      {isActive ? (
        <div className="bg-raizama-surface border border-raizama-action/30 shadow-[0_0_30px_rgba(16,185,129,0.1)] rounded-[2.5rem] p-6 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-raizama-action text-white text-[10px] font-black uppercase px-4 py-1.5 rounded-bl-[2rem] shadow-lg">
            PRO
          </div>
          <h3 className="text-xs font-black text-white uppercase tracking-widest mb-1">Status: Assinatura Ativa</h3>
          <p className="text-sm text-raizama-action font-bold mb-4">
            Plano {subscription.plan_type === 'yearly' ? 'Anual' : 'Mensal'}
          </p>
          
          <div className="bg-black/30 rounded-2xl p-4 mb-6 border border-white/5">
            <p className="text-white text-sm font-bold flex items-center gap-2">
              <Check size={16} className="text-raizama-action" /> 
              Sua coleção possui catálogo ILIMITADO
            </p>
            <p className="text-raizama-textSec text-[10px] mt-2 ml-6 uppercase font-bold tracking-widest">
              Renovação: {new Date(subscription.current_period_end).toLocaleDateString()}
            </p>
          </div>

          <button 
            onClick={handleManageSubscription}
            disabled={loading !== null}
            className="w-full py-4 bg-white/5 hover:bg-white/10 text-white font-black rounded-2xl uppercase tracking-widest text-xs transition-colors flex justify-center items-center gap-2 border border-white/10"
          >
            {loading === 'portal' ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
            Gerenciar Assinatura
          </button>
        </div>
      ) : (
        <div className="bg-raizama-surface border border-raizama-border/30 shadow-xl rounded-[2.5rem] p-6 mb-8">
          <h3 className="text-xs font-black text-white uppercase tracking-widest mb-1">Plano Atual: Gratuito</h3>
          <p className="text-raizama-textSec text-xs mb-6">Limites da sua coleção</p>
          
          <div className="mb-2 flex justify-between items-end">
            <span className="text-white font-bold text-sm">{plantsCount} de 50</span>
            <span className="text-raizama-textSec text-[10px] uppercase font-black tracking-widest">Plantas Catalogadas</span>
          </div>
          <div className="w-full bg-black/40 rounded-full h-3 mb-6 overflow-hidden border border-white/5">
            <div 
              className={`h-full rounded-full transition-all duration-1000 ${plantsCount >= 50 ? 'bg-red-500' : 'bg-raizama-action'}`} 
              style={{ width: `${Math.min((plantsCount / 50) * 100, 100)}%` }}
            />
          </div>

          {plantsCount >= 50 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mb-6 flex gap-3 animate-pulse">
              <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={20} />
              <p className="text-red-400 text-xs font-bold leading-relaxed">
                Você atingiu o limite de 50 plantas do plano gratuito. Assine um plano para continuar adicionando novas plantas.
              </p>
            </div>
          )}
        </div>
      )}

      {!isActive && (
        <div className="space-y-6">
          <h3 className="text-sm font-black text-white uppercase tracking-widest px-2 mb-2">Faça o Upgrade</h3>
          {/* Mensal */}
          <div className="bg-raizama-surface rounded-[2rem] p-6 border border-raizama-border/30 shadow-xl relative">
            <h4 className="text-white font-black text-lg mb-1 uppercase">Plano Mensal</h4>
            <div className="flex items-end gap-1 mb-6">
              <span className="text-3xl font-black text-white">R$ 9,99</span>
              <span className="text-raizama-textSec text-sm font-bold pb-1">/mês</span>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-xs text-raizama-textSec">
                <Check size={14} className="text-raizama-action shrink-0" /> Catálogo ILIMITADO de plantas
              </li>
              <li className="flex items-center gap-2 text-xs text-raizama-textSec">
                <Check size={14} className="text-raizama-action shrink-0" /> Histórico completo de cuidados
              </li>
              <li className="flex items-center gap-2 text-xs text-raizama-textSec">
                <Check size={14} className="text-raizama-action shrink-0" /> Consultas IA ilimitadas
              </li>
            </ul>
            <button 
              onClick={() => handleSubscribe('monthly')}
              disabled={loading !== null}
              className="w-full py-4 bg-white/5 hover:bg-white/10 text-white font-black rounded-2xl uppercase tracking-widest text-xs transition-colors flex justify-center items-center h-14 border border-white/5"
            >
              {loading === 'monthly' ? <Loader2 className="animate-spin" size={16} /> : 'Assinar Mensal'}
            </button>
          </div>

          {/* Anual */}
          <div className="bg-raizama-action/10 rounded-[2rem] p-6 border border-raizama-action/50 shadow-[0_0_20px_rgba(16,185,129,0.15)] relative overflow-hidden">
            <div className="absolute top-4 right-4 bg-raizama-action text-white text-[9px] font-black uppercase px-3 py-1 rounded-full tracking-widest shadow-lg">
              Economia de 20%
            </div>
            <h4 className="text-white font-black text-lg mb-1 uppercase">Plano Anual</h4>
            <div className="flex items-end gap-1 mb-6">
              <span className="text-3xl font-black text-raizama-action">R$ 99,99</span>
              <span className="text-raizama-textSec text-sm font-bold pb-1">/ano</span>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-xs text-raizama-textSec">
                <Check size={14} className="text-raizama-action shrink-0" /> Catálogo ILIMITADO de plantas
              </li>
              <li className="flex items-center gap-2 text-xs text-raizama-textSec">
                <Check size={14} className="text-raizama-action shrink-0" /> Recursos exclusivos IA Plants
              </li>
              <li className="flex items-center gap-2 text-xs text-raizama-textSec">
                <Check size={14} className="text-raizama-action shrink-0" /> Suporte prioritário
              </li>
            </ul>
            <button 
              onClick={() => handleSubscribe('yearly')}
              disabled={loading !== null}
              className="w-full py-4 bg-raizama-action hover:brightness-110 active:scale-95 text-white font-black rounded-2xl uppercase tracking-widest text-xs transition-all shadow-xl shadow-raizama-action/20 flex justify-center items-center h-14"
            >
              {loading === 'yearly' ? <Loader2 className="animate-spin" size={16} /> : 'Assinar Anual'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
